import { Table } from "antd";
import moment from "moment";
import { useState } from "react";
import { type RouteComponentProps } from "react-router";

import { IconCaretLargeRight } from "@btnet/blueprint-icons";
import { BTText } from "@btnet/core-components/BTText";
import { cn } from "@btnet/utilities-core";

import { PaymentStatus } from "types/enum";

import { lazyLoadComponent } from "utilities/LazyLoad.utility";
import { sum } from "utilities/math/math";
import { routes } from "utilities/routes";

import { BTLink, BTLinkRelative } from "commonComponents/btWrappers/BTLink/BTLink";
import { type IModalConfiguration } from "commonComponents/btWrappers/BTModal/BTModal";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { BTTable, type IBTColumnProps } from "commonComponents/btWrappers/BTTable/BTTable";
import { BTTitle } from "commonComponents/btWrappers/BTTitle/BTTitle";
import { IDTypes } from "commonComponents/entity/accounting/common/accounting.api.types";
import { MerchantPaymentStatusTag } from "commonComponents/entity/merchantPayment/common/MerchantPaymentStatus/MerchantPaymentStatus";
import { CurrencyDisplay } from "commonComponents/financial/CurrencyDisplay/CurrencyDisplay";
import { DateDisplayDate } from "commonComponents/utilities/DateDisplay/DateDisplayDate";
import "commonComponents/utilities/LineItemContainerV2/LineItemTotal/LineItemTotal.less";
import { PageSection } from "commonComponents/utilities/PageSection/PageSection";
import { StatusTagDisplay } from "commonComponents/utilities/Status/StatusTagDisplay";

import { BillCollapsibleSection } from "entity/bill/Bill/BillCollapsibleSection/BillCollapsibleSection";
import { RouteBillPayment } from "entity/bill/BillPayment/RouteBillPayment";
import "entity/invoicePayment/InvoicePaymentHistory/PaymentHistory.less";

const LinkEntityToAccounting = lazyLoadComponent(
    () =>
        import("commonComponents/entity/accounting/LinkEntityToAccounting/LinkEntityToAccounting"),
);

/** Mirrors the server's BillPaymentMethodType, sent as each row's `rowType`. */
export enum BillPaymentRowType {
    OnlinePayment = 0,
    VendorCreditApplied = 1,
    OfflinePayment = 2,
}

export interface IBillPaymentForListData {
    paymentId: number;
    addedDate: string;
    scheduledDate?: string | null;
    paymentAmount: number;
    checkNumber: string;
    paymentMethod: string | null;
    status: PaymentStatus;
    /** Integer parent PK for vendor-credit settlements and offline payments. Used by v3 API routes. */
    parentBillPaymentId?: number | null;
    /** True when this row is a vendor-credit application (routes by parentBillPaymentId, status shows "Applied"). */
    isVendorCreditApplied?: boolean;
    rowType?: BillPaymentRowType;
    merchantPaymentMethodId?: number | null;
    merchantPaymentMethodName?: string | null;
    sentToAccounting?: boolean | null;
    isBankAccountLinkedToAccounting?: boolean | null;
}

export class BillPaymentForList {
    constructor(data: IBillPaymentForListData) {
        this.paymentId = data.paymentId;
        this.addedDate = moment(data.addedDate);
        this.scheduledDate = data.scheduledDate ? moment(data.scheduledDate) : undefined;
        this.paymentAmount = data.paymentAmount;
        this.checkNumber = data.checkNumber;
        this.paymentMethod = data.paymentMethod ?? "";
        this.status = data.status;
        this.parentBillPaymentId = data.parentBillPaymentId ?? null;
        this.isVendorCreditApplied = data.isVendorCreditApplied ?? false;
        this.rowType =
            data.rowType ??
            (this.isVendorCreditApplied
                ? BillPaymentRowType.VendorCreditApplied
                : BillPaymentRowType.OnlinePayment);
        this.merchantPaymentMethodId = data.merchantPaymentMethodId ?? null;
        this.merchantPaymentMethodName = data.merchantPaymentMethodName ?? "";
        this.sentToAccounting = data.sentToAccounting ?? null;
        this.isBankAccountLinkedToAccounting = data.isBankAccountLinkedToAccounting ?? null;
    }
    paymentId: number;
    addedDate: moment.Moment;
    scheduledDate: moment.Moment | undefined;
    paymentAmount: number;
    checkNumber: string;
    paymentMethod: string;
    status: PaymentStatus;
    parentBillPaymentId: number | null;
    isVendorCreditApplied: boolean;
    rowType: BillPaymentRowType;
    merchantPaymentMethodId: number | null;
    merchantPaymentMethodName: string;
    sentToAccounting: boolean | null;
    isBankAccountLinkedToAccounting: boolean | null;
}

const NON_COUNTING_STATUSES = [
    PaymentStatus.Expired,
    PaymentStatus.Failed,
    PaymentStatus.Refunded,
    PaymentStatus.Void,
];

const getPaymentTotal = (rows: readonly BillPaymentForList[]) =>
    sum(
        rows.map((row) => (NON_COUNTING_STATUSES.includes(row.status) ? 0 : row.paymentAmount)),
    ).toNumber();

const getPaymentDetailPath = (record: BillPaymentForList): string | undefined => {
    // Vendor-credit settlements and offline payments have no merchant payment, so they open by their BillPayments parent.
    if (record.rowType !== BillPaymentRowType.OnlinePayment) {
        return record.parentBillPaymentId
            ? routes.billPayment.getDetailByParentBillPaymentIdPath(record.parentBillPaymentId)
            : undefined;
    }

    return routes.billPayment.getDetailsPath(record.paymentId);
};

const getPaymentRowKey = (record: BillPaymentForList) => {
    if (record.isVendorCreditApplied && record.parentBillPaymentId) {
        return `vc-${record.parentBillPaymentId}`;
    }

    if (record.rowType === BillPaymentRowType.OfflinePayment && record.parentBillPaymentId) {
        return `offline-${record.parentBillPaymentId}`;
    }

    return record.paymentId.toString();
};

const getScheduledStatusReason = (scheduledDate: moment.Moment, isSubPortal: boolean) => {
    const sendsOn = `This payment is scheduled to send on ${scheduledDate.format("MMMM D, YYYY")}.`;
    return isSubPortal
        ? sendsOn
        : `${sendsOn} To cancel, void the payment before the scheduled date.`;
};

const PaymentStatusTag = ({
    record,
    isSubPortal,
}: {
    record: BillPaymentForList;
    isSubPortal: boolean;
}) => {
    if (record.isVendorCreditApplied) {
        return (
            <StatusTagDisplay
                statusType="success"
                statusText="Applied"
                data-testid="billPaymentStatusApplied"
            />
        );
    }

    if (
        record.rowType === BillPaymentRowType.OfflinePayment &&
        record.status !== PaymentStatus.Void
    ) {
        return (
            <StatusTagDisplay
                statusType="success"
                statusText="Paid"
                data-testid="billPaymentStatusPaid"
            />
        );
    }

    return (
        <MerchantPaymentStatusTag
            status={record.status}
            statusReason={
                record.status === PaymentStatus.Scheduled && record.scheduledDate
                    ? getScheduledStatusReason(record.scheduledDate, isSubPortal)
                    : undefined
            }
        />
    );
};

interface IAccountingStatusProps {
    record: BillPaymentForList;
    integrationName: string | null;
}

const AccountingStatusTag = ({ record, integrationName }: IAccountingStatusProps) => {
    if (record.sentToAccounting === null) {
        return null;
    }

    const suffix = integrationName ? ` to ${integrationName}` : "";
    return record.sentToAccounting ? (
        <StatusTagDisplay
            statusType="info"
            statusText={`Sent${suffix}`}
            data-testid="billPaymentAccountingStatusSent"
        />
    ) : (
        <StatusTagDisplay
            statusType=""
            statusText={`Not sent${suffix}`}
            data-testid="billPaymentAccountingStatusNotSent"
        />
    );
};

interface IAccountingLinkPromptProps {
    record: BillPaymentForList;
    linkedMethodIds: ReadonlySet<number>;
    onLinkAccount: (merchantPaymentMethodId: number) => void;
}

const getUnlinkedMethodId = (
    record: BillPaymentForList,
    linkedMethodIds: ReadonlySet<number>,
): number | null =>
    record.sentToAccounting === false &&
    record.isBankAccountLinkedToAccounting === false &&
    record.merchantPaymentMethodId !== null &&
    !linkedMethodIds.has(record.merchantPaymentMethodId)
        ? record.merchantPaymentMethodId
        : null;

const AccountingLinkPrompt = ({
    record,
    linkedMethodIds,
    onLinkAccount,
}: IAccountingLinkPromptProps) => {
    const methodId = getUnlinkedMethodId(record, linkedMethodIds);
    if (methodId === null) {
        return null;
    }

    return (
        <BTText size="normal-sm" className="text-gray-70">
            Bank account not linked{" "}
            <BTLink
                to=""
                href=""
                data-testid="billPaymentLinkAccount"
                onClick={(e) => {
                    e.preventDefault();
                    onLinkAccount(methodId);
                }}
            >
                Link account
            </BTLink>
        </BTText>
    );
};

const paymentCardClassName =
    "flex flex-col gap-2 rounded-md border border-default p-3 text-fg-default";
const paymentCardRowClassName = "flex items-center justify-between gap-2";

interface IPaymentCardProps {
    record: BillPaymentForList;
    integrationName: string | null;
    linkedMethodIds: ReadonlySet<number>;
    onLinkAccount: (merchantPaymentMethodId: number) => void;
    isSubPortal: boolean;
}

const PaymentCard = ({
    record,
    integrationName,
    linkedMethodIds,
    onLinkAccount,
    isSubPortal,
}: IPaymentCardProps) => {
    const isScheduled = record.status === PaymentStatus.Scheduled && record.scheduledDate;
    const detailPath = getPaymentDetailPath(record);

    const body = (
        <>
            <div className="flex min-w-0 flex-1 flex-col items-start gap-1">
                <PaymentStatusTag record={record} isSubPortal={isSubPortal} />
                {integrationName && (
                    <AccountingStatusTag record={record} integrationName={integrationName} />
                )}
                <BTText size="normal-sm" className="truncate text-gray-70">
                    {isScheduled ? (
                        <>
                            Sends{" "}
                            <DateDisplayDate
                                value={record.scheduledDate!.toDate()}
                                data-testid="billPaymentScheduledDate"
                            />
                        </>
                    ) : (
                        <DateDisplayDate
                            value={record.addedDate.toDate()}
                            data-testid="billPaymentAddedDate"
                        />
                    )}
                    {record.paymentMethod && ` \u00b7 ${record.paymentMethod}`}
                </BTText>
            </div>
            <div className="flex shrink-0 items-center gap-2">
                <BTText size="distinct-sm" className="tabular-nums">
                    <b>
                        <CurrencyDisplay
                            value={record.paymentAmount}
                            data-testid="billPaymentAmount"
                        />
                    </b>
                </BTText>
                {detailPath && (
                    <span
                        className="inline-flex text-gray-70"
                        aria-hidden="true"
                        data-testid="billPaymentCardCaret"
                    >
                        <IconCaretLargeRight size="md" />
                    </span>
                )}
            </div>
        </>
    );

    return (
        <div className={paymentCardClassName}>
            {detailPath ? (
                <BTLinkRelative
                    to={detailPath}
                    isUnderline={false}
                    data-testid="billPaymentCard"
                    className={cn(paymentCardRowClassName, "hover:text-fg-default")}
                >
                    {body}
                </BTLinkRelative>
            ) : (
                <div data-testid="billPaymentCard" className={paymentCardRowClassName}>
                    {body}
                </div>
            )}
            <AccountingLinkPrompt
                record={record}
                linkedMethodIds={linkedMethodIds}
                onLinkAccount={onLinkAccount}
            />
        </div>
    );
};

interface IBillPaymentHistoryProps extends RouteComponentProps {
    payments: BillPaymentForList[];
    entityName: string;
    onReload: () => Promise<void>;
    isMobileLayout?: boolean;
    isBuilder: boolean;
    accountingIntegrationName?: string | null;
    /** Omits payer-only guidance (e.g. voiding) that doesn't apply to subs. */
    isSubPortal?: boolean;
}

export const BillPaymentHistory: React.FunctionComponent<IBillPaymentHistoryProps> = ({
    payments,
    entityName,
    history,
    match,
    onReload,
    isMobileLayout = false,
    isBuilder,
    accountingIntegrationName = null,
    isSubPortal = false,
}) => {
    const hasPayments = payments.length > 0;
    const showAccountingStatus = isBuilder && accountingIntegrationName !== null;
    const [linkingMethodId, setLinkingMethodId] = useState<number | null>(null);
    const [linkedMethodIds, setLinkedMethodIds] = useState<ReadonlySet<number>>(() => new Set());

    // LinkEntityToAccounting suppresses its own close when onSubmit is supplied, so every outcome closes here.
    // No reload: linking a bank account doesn't sync its payments, and Bill's reload discards unsaved form edits.
    const handleLinkSubmit = (isLinked: boolean) => {
        if (isLinked && linkingMethodId !== null) {
            setLinkedMethodIds((prev) => new Set(prev).add(linkingMethodId));
        }
        setLinkingMethodId(null);
    };

    const billPaymentHistoryColumns: IBTColumnProps<BillPaymentForList>[] = [];

    const modalConfig: IModalConfiguration = {
        beforeClose: () => {
            void onReload();
            history.replace(match.url);
        },
        parentRoute: match.url,
    };

    billPaymentHistoryColumns.push({
        dataIndex: "addedDate",
        title: "Date",
        width: 140,
        render: (paymentDate: moment.Moment, record: BillPaymentForList) =>
            record.status === PaymentStatus.Scheduled && record.scheduledDate ? (
                <span>
                    Scheduled date:{" "}
                    <DateDisplayDate
                        value={record.scheduledDate.toDate()}
                        data-testid="billPaymentScheduledDate"
                    />
                </span>
            ) : (
                <DateDisplayDate value={paymentDate.toDate()} data-testid="billPaymentAddedDate" />
            ),
        className: "VerticalAlignRow",
    });

    billPaymentHistoryColumns.push({
        dataIndex: "paymentAmount",
        title: "Payment amount",
        align: "right",
        render: (paymentAmount: number) => (
            <CurrencyDisplay value={paymentAmount} data-testid="billPaymentAmount" />
        ),
        className: "VerticalAlignRow",
    });

    billPaymentHistoryColumns.push({
        dataIndex: "paymentMethod",
        title: "Payment method",
        className: "VerticalAlignRow",
    });

    if (payments.some((p) => p.merchantPaymentMethodName)) {
        billPaymentHistoryColumns.push({
            dataIndex: "merchantPaymentMethodName",
            title: "Sent from",
            className: "VerticalAlignRow",
        });
    }

    billPaymentHistoryColumns.push({
        dataIndex: "status",
        title: "Payment status",
        render: (_status: PaymentStatus, record: BillPaymentForList) => (
            <PaymentStatusTag record={record} isSubPortal={isSubPortal} />
        ),
        className: "VerticalAlignRow",
    });

    if (showAccountingStatus) {
        billPaymentHistoryColumns.push({
            dataIndex: "sentToAccounting",
            title: "Accounting status",
            render: (_sent: boolean | null, record: BillPaymentForList) => (
                <div className="flex flex-col items-start gap-1">
                    <AccountingStatusTag
                        record={record}
                        integrationName={accountingIntegrationName}
                    />
                    <AccountingLinkPrompt
                        record={record}
                        linkedMethodIds={linkedMethodIds}
                        onLinkAccount={setLinkingMethodId}
                    />
                </div>
            ),
            className: "VerticalAlignRow",
        });
    }

    billPaymentHistoryColumns.push({
        dataIndex: "paymentId",
        render: (_paymentId: number, record: BillPaymentForList) => {
            const detailPath = getPaymentDetailPath(record);
            return detailPath ? (
                <BTLinkRelative to={detailPath}>View details</BTLinkRelative>
            ) : null;
        },
    });

    const content = (
        <>
            {!hasPayments && (
                <span data-testid="billPaymentHistoryEmptyStateText">
                    Payments applied to this {entityName} will be listed here
                </span>
            )}
            {hasPayments && isMobileLayout && (
                <div className="flex flex-col gap-2 pt-2" data-testid="billPaymentCards">
                    {payments.map((record) => (
                        <PaymentCard
                            key={getPaymentRowKey(record)}
                            record={record}
                            integrationName={
                                showAccountingStatus ? accountingIntegrationName : null
                            }
                            linkedMethodIds={linkedMethodIds}
                            onLinkAccount={setLinkingMethodId}
                            isSubPortal={isSubPortal}
                        />
                    ))}
                    <div className="LineItemTotal">
                        <div className="LineItemTotalRow">
                            <BTText size="distinct-sm">Total:</BTText>
                            <BTText size="distinct-sm">
                                <b>
                                    <CurrencyDisplay
                                        value={getPaymentTotal(payments)}
                                        data-testid="billPaymentTotal"
                                    />
                                </b>
                            </BTText>
                        </div>
                    </div>
                </div>
            )}
            {hasPayments && !isMobileLayout && (
                <div className="BillPaymentHistoryScrollable">
                    <BTTable
                        dataSource={payments}
                        rowKey={getPaymentRowKey}
                        columns={billPaymentHistoryColumns}
                        size="small"
                        pagination={false}
                        summary={(data) => (
                            <Table.Summary>
                                <Table.Summary.Row>
                                    {billPaymentHistoryColumns.map((x, index) => {
                                        if (x.title === "Payment amount") {
                                            return (
                                                <Table.Summary.Cell
                                                    key={index}
                                                    index={index}
                                                    className="text-right"
                                                >
                                                    <strong>
                                                        Total:{" "}
                                                        <CurrencyDisplay
                                                            value={getPaymentTotal(data)}
                                                            data-testid="billPaymentTotal"
                                                        />
                                                    </strong>
                                                </Table.Summary.Cell>
                                            );
                                        } else {
                                            return <Table.Summary.Cell key={index} index={index} />;
                                        }
                                    })}
                                </Table.Summary.Row>
                            </Table.Summary>
                        )}
                    />
                </div>
            )}
        </>
    );

    return (
        <div data-testid="billPaymentHistory">
            <RouteBillPayment modalConfig={modalConfig} fromBill={true} />
            {linkingMethodId !== null && (
                <LinkEntityToAccounting
                    idType={IDTypes.PaymentMethodBankAccount}
                    entityID={linkingMethodId}
                    modalTitle={`Edit Link to ${accountingIntegrationName ?? ""}`}
                    onSubmit={handleLinkSubmit}
                    onCancel={() => setLinkingMethodId(null)}
                    modalConfig={{
                        parentRoute: "",
                        beforeClose: () => setLinkingMethodId(null),
                    }}
                />
            )}
            {isMobileLayout ? (
                <BillCollapsibleSection
                    title="Payment history"
                    testid="paymentHistoryCollapsible"
                    defaultOpen={false}
                >
                    {content}
                </BillCollapsibleSection>
            ) : (
                <PageSection>
                    <BTRow>
                        <BTTitle level={3}>Payment history</BTTitle>
                    </BTRow>
                    {content}
                </PageSection>
            )}
        </div>
    );
};
